import { Center, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useState } from "react"
import { useWorkShiftsByKeywordQuery, WorkShiftsByKeywordQueryVariables } from "../../graphql"
import { WorkShiftsList } from "./WorkShiftsList"

export type WorkShiftsByKeywordProps = {
	keyword: string
}

export const WorkShiftsByKeyword: React.FC<WorkShiftsByKeywordProps> = ({ keyword }) => {
	const [filter, setFilter] = useState({ keyword })

	const [pagination, setPagination] = useState<WorkShiftsByKeywordQueryVariables["pagination"]>({ limit: 20, page: 1 })

	const [{ data, fetching, error }] = useWorkShiftsByKeywordQuery({
		variables: { ...filter, pagination },
	})

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter({ keyword })
		}, 1000)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword])

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.workShiftsByKeyword.hasNextPage) {
			setPagination((prev) => ({
				...prev,
				page: (prev.page || 0) + 1,
			}))
		}
	}, [data, fetching])

	return (
		<VStack w="full" maxW="2xl" align="stretch">
			{fetching && !data?.workShiftsByKeyword ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading work shifts</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.workShiftsByKeyword.workShifts && data.workShiftsByKeyword.workShifts.length ? (
				<VStack w="full" align="stretch">
					<Text>
						Showing {data.workShiftsByKeyword.workShifts.length || 0} work shift
						{(data.workShiftsByKeyword.workShifts.length || 0) > 1 ? "s" : ""}
					</Text>

					<WorkShiftsList workShifts={data.workShiftsByKeyword.workShifts} loadMore={nextPage} />

					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more work shifts</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any work shifts.
					</Text>
				</Center>
			)}
		</VStack>
	)
}
